import { AuthRole, roleManager } from '../rbac/roleManager';
import { logger } from '@shared/logging/logger';

export interface SecurityContext {
  userId?: string;
  role: AuthRole;
  statusAprovacao?: string;
  ecopontoId?: string;
  [key: string]: any;
}

export type PermissionPolicy = (context: SecurityContext, resource: any) => boolean;

class PermissionManager {
  private policies: Map<string, PermissionPolicy> = new Map();

  constructor() {
    this.registerDefaultPolicies();
  }

  /**
   * Register a custom policy for a fine-grained permission action.
   */
  public registerPolicy(permission: string, policy: PermissionPolicy): void {
    this.policies.set(permission, policy);
    logger.debug('security', `Custom policy registered for: ${permission}`);
  }

  /**
   * Validates if a subject can execute an action on a specific resource.
   */
  public can(context: SecurityContext, permission: string, resource?: any): boolean {
    // 1. Basic RBAC check
    const hasRoleAuth = roleManager.hasPermission(context.role, permission);
    if (!hasRoleAuth) {
      return false;
    }

    // 2. Status check of collector/company profiles
    if (
      (context.role === 'collector' || context.role === 'company') &&
      context.statusAprovacao &&
      context.statusAprovacao !== 'Aprovado'
    ) {
      // Disallow operational permissions if not approved (except viewing statistics or basic actions)
      const allowedUnapproved = [
        'shared:view_profile',
        'shared:update_profile',
        'shared:view_notifications',
      ];
      if (!allowedUnapproved.includes(permission)) {
        return false;
      }
    }

    // 3. Dynamic policy check
    const policy = this.policies.get(permission);
    if (policy) {
      try {
        return policy(context, resource);
      } catch (err) {
        logger.error('security', `Error evaluating security policy for ${permission}`, err);
        return false; // Fail-secure
      }
    }

    return true;
  }

  /**
   * Registers default business validation policies.
   */
  private registerDefaultPolicies(): void {
    // Self-profile edit policy
    this.registerPolicy('shared:update_profile', (context, resource) => {
      if (!resource || !context.userId) return false;
      // Users can only update their own profile unless they are manager
      if (context.role === 'manager') return true;
      return resource.userId === context.userId;
    });

    // Discard registration validation: Only attendant assigned to the specific Ecoponto can do it,
    // or manager has overrides.
    this.registerPolicy('app:register_discard', (context, resource) => {
      if (context.role === 'manager') return true;
      if (context.role !== 'attendant') return false;
      if (!resource || !resource.ecopontoId) return true; // generic check
      return context.ecopontoId === resource.ecopontoId;
    });

    // Request Collection restriction: Compania must have status Approved
    this.registerPolicy('app:request_collection', (context, _resource) => {
      if (context.role === 'manager') return true;
      return context.statusAprovacao === 'Aprovado';
    });
  }
}

export const permissionManager = new PermissionManager();
export default permissionManager;
